import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import {
  canApproveAppIssueAgentJob,
  formatAppIssueAgentStatusLabel,
  useAddAppIssueTicketMessage,
  useAppIssueTicketAgentJobs,
  useAppIssueTicketMessages,
  useAppIssueTickets,
  useDecideAppIssueAgentJob,
  useUpdateAppIssueTicket,
  type AppIssueStatus,
  type AppIssueTicket,
  type AppIssueTicketAgentJob,
} from '../hooks/useAppIssueTickets';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
  display: grid;
  gap: 1rem;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  padding: 1rem 1.1rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.45rem;
  color: var(--onSurface);
  font-size: 0.9rem;
  max-width: 280px;
`;

const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.7rem 0.85rem;
  font: inherit;
`;

const Textarea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.75rem 0.85rem;
  font: inherit;
  min-height: 96px;
  resize: vertical;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Button = styled.button`
  border: none;
  border-radius: 9999px;
  padding: 0.72rem 1.1rem;
  background: var(--primary);
  color: var(--onPrimary);
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 9999px;
  padding: 0.72rem 1.1rem;
  background: var(--surface);
  color: var(--onSurface);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
`;

const DangerButton = styled(SecondaryButton)`
  border-color: color-mix(in srgb, #dc2626 45%, var(--outline));
  color: #b91c1c;
`;

const AgentCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  background: color-mix(in srgb, var(--primary) 6%, var(--surface));
  display: grid;
  gap: 0.7rem;
`;

const AgentLink = styled.a`
  color: var(--primary);
  font-size: 0.9rem;
  word-break: break-all;
`;

const FeedbackBanner = styled.p`
  margin: 0;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: color-mix(in srgb, var(--primary) 12%, transparent);
  color: var(--onSurface);
  font-size: 0.9rem;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
    vertical-align: top;
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: var(--surfaceVariant);
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MessageCell = styled.td`
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AgentPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: var(--surfaceVariant);
  color: var(--onSurface);
`;

const StatusPill = styled.span<{ $status: AppIssueStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'resolved'
      ? 'color-mix(in srgb, #16a34a 18%, transparent)'
      : $status === 'closed'
        ? 'var(--surfaceVariant)'
        : $status === 'in_progress'
          ? 'color-mix(in srgb, #2563eb 18%, transparent)'
          : 'color-mix(in srgb, var(--primary) 18%, transparent)'};
  color: var(--onSurface);
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(840px, 100%);
  max-height: min(88vh, 900px);
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.15rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  gap: 1rem;
  overflow: auto;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.9rem;
`;

const DetailGroup = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DetailLabel = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Thread = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const ThreadItem = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.75rem 0.85rem;
  background: var(--surfaceVariant);
`;

const ThreadMeta = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const STATUS_OPTIONS: Array<{ value: AppIssueStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const formatStatusLabel = (status: AppIssueStatus) =>
  STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;

const formatCategoryLabel = (category: AppIssueTicket['category']) => {
  if (category === 'feature') return 'Feature';
  if (category === 'feedback') return 'Feedback';
  if (category === 'other') return 'Other';
  return 'Bug';
};

const formatIntentLabel = (intent?: AppIssueTicketAgentJob['intent']) => {
  if (intent === 'code_fix') return 'Code fix';
  if (intent === 'communication') return 'Communication';
  if (intent === 'unclear') return 'Unclear';
  return 'Pending';
};

const AppIssuesPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tickets = [], isLoading, error } = useAppIssueTickets();
  const { data: agentJobs = [] } = useAppIssueTicketAgentJobs();
  const updateTicketMutation = useUpdateAppIssueTicket();
  const addMessageMutation = useAddAppIssueTicketMessage();
  const decideAgentJobMutation = useDecideAppIssueAgentJob();

  const [statusFilter, setStatusFilter] = useState<AppIssueStatus | 'all'>('all');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<AppIssueTicket | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<AppIssueStatus>('open');
  const [replyDraft, setReplyDraft] = useState('');

  const { data: messages = [], isLoading: messagesLoading } = useAppIssueTicketMessages(selectedTicket?.id);

  const jobsByTicketId = useMemo(() => {
    const next = new Map<string, AppIssueTicketAgentJob>();
    for (const job of agentJobs) {
      next.set(job.ticket_id, job);
    }
    return next;
  }, [agentJobs]);

  const selectedJob = selectedTicket ? jobsByTicketId.get(selectedTicket.id) ?? null : null;
  const canApproveFix = canApproveAppIssueAgentJob(selectedJob);
  const canRejectJob =
    selectedJob?.workflow_status === 'awaiting_approval' ||
    selectedJob?.workflow_status === 'communication_only';

  const filteredTickets = useMemo(() => {
    if (statusFilter === 'all') return tickets;
    return tickets.filter((ticket) => ticket.status === statusFilter);
  }, [statusFilter, tickets]);

  useEffect(() => {
    if (!selectedTicket) return;
    const latest = tickets.find((ticket) => ticket.id === selectedTicket.id);
    if (latest) {
      setSelectedTicket(latest);
      setSelectedStatus(latest.status);
    }
  }, [selectedTicket, tickets]);

  useEffect(() => {
    const ticketId = searchParams.get('ticket');
    if (!ticketId || selectedTicket?.id === ticketId) return;
    const match = tickets.find((ticket) => ticket.id === ticketId);
    if (match) {
      setSelectedTicket(match);
      setSelectedStatus(match.status);
      setReplyDraft('');
    }
  }, [searchParams, selectedTicket?.id, tickets]);

  const closeTicket = () => {
    setSelectedTicket(null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('ticket');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setSelectedTicket(null);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('ticket');
        return next;
      }, { replace: true });
    };

    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [setSearchParams]);

  const openTicket = (ticket: AppIssueTicket) => {
    setSelectedTicket(ticket);
    setSelectedStatus(ticket.status);
    setReplyDraft('');
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('ticket', ticket.id);
      return next;
    }, { replace: true });
  };

  const saveSelectedStatus = async () => {
    if (!selectedTicket) return;
    try {
      const updated = await updateTicketMutation.mutateAsync({
        id: selectedTicket.id,
        status: selectedStatus,
      });
      setSelectedTicket(updated);
      setFeedback(`Ticket "${updated.subject}" updated.`);
    } catch (saveError) {
      setFeedback(saveError instanceof Error ? saveError.message : 'Could not update ticket.');
    }
  };

  const sendReply = async () => {
    if (!selectedTicket || !replyDraft.trim()) return;
    const authorName = profile?.full_name || user?.email || 'Super admin';
    const authorId = user?.id;
    if (!authorId) {
      setFeedback('Sign in again to reply.');
      return;
    }

    try {
      await addMessageMutation.mutateAsync({
        ticketId: selectedTicket.id,
        body: replyDraft.trim(),
        authorName,
        authorId,
      });
      setReplyDraft('');
      setFeedback(`Reply added to "${selectedTicket.subject}".`);
    } catch (saveError) {
      setFeedback(saveError instanceof Error ? saveError.message : 'Could not send reply.');
    }
  };

  const decideAgentJob = async (action: 'approve' | 'reject') => {
    if (!selectedTicket) return;
    try {
      await decideAgentJobMutation.mutateAsync({ ticketId: selectedTicket.id, action });
      setFeedback(
        action === 'approve'
          ? `Approved a Cursor Cloud fix for "${selectedTicket.subject}".`
          : `Rejected the suggested fix for "${selectedTicket.subject}".`,
      );
    } catch (decideError) {
      setFeedback(decideError instanceof Error ? decideError.message : 'Could not update the agent job.');
    }
  };

  return (
    <Page>
      <div>
        <HeaderTitle>App Issues</HeaderTitle>
        <HelperText>
          Review bugs, feedback, and feature requests submitted from the mobile app. Cursor Cloud
          analysis appears here after a ticket is submitted. Approve a fix after you review it —
          including migration or edge-function files. Applying migrations and deploying functions
          still stays with you.
        </HelperText>
      </div>

      <Field>
        Status
        <Select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as AppIssueStatus | 'all')}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      {feedback ? <FeedbackBanner role="status">{feedback}</FeedbackBanner> : null}

      {isLoading && <HelperText>Loading app issues…</HelperText>}
      {error && (
        <HelperText style={{ color: 'var(--error)' }}>
          Could not load app issues. Make sure the new migration has been applied.
        </HelperText>
      )}

      {!isLoading && filteredTickets.length > 0 && (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Reporter</th>
                  <th>Status</th>
                  <th>Agent</th>
                  <th>App</th>
                  <th>Description</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => (
                  <ClickableRow
                    key={ticket.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openTicket(ticket)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openTicket(ticket);
                      }
                    }}
                  >
                    <td>{ticket.subject}</td>
                    <td>{formatCategoryLabel(ticket.category)}</td>
                    <td>{ticket.reporter_name || '—'}</td>
                    <td>
                      <StatusPill $status={ticket.status}>{formatStatusLabel(ticket.status)}</StatusPill>
                    </td>
                    <td>
                      <AgentPill>
                        {formatAppIssueAgentStatusLabel(jobsByTicketId.get(ticket.id)?.workflow_status)}
                      </AgentPill>
                    </td>
                    <td>
                      {ticket.platform}
                      {ticket.app_version ? ` · v${ticket.app_version}` : ''}
                    </td>
                    <MessageCell title={ticket.description}>{ticket.description}</MessageCell>
                    <td>{ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '—'}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      )}

      {!isLoading && !error && filteredTickets.length === 0 && (
        <Card>
          <HelperText>No app issue tickets match this filter.</HelperText>
        </Card>
      )}

      {selectedTicket ? (
        <ModalOverlay onClick={closeTicket}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>App Issue Details</ModalTitle>
              <ModalCloseButton
                type="button"
                aria-label="Close app issue details"
                onClick={closeTicket}
              >
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailGrid>
                <DetailGroup>
                  <DetailLabel>Subject</DetailLabel>
                  <DetailValue>{selectedTicket.subject}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Category</DetailLabel>
                  <DetailValue>{formatCategoryLabel(selectedTicket.category)}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Reporter</DetailLabel>
                  <DetailValue>{selectedTicket.reporter_name || '—'}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Contact</DetailLabel>
                  <DetailValue>{selectedTicket.reporter_email || '—'}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Platform</DetailLabel>
                  <DetailValue>
                    {selectedTicket.platform}
                    {selectedTicket.app_version ? ` · v${selectedTicket.app_version}` : ''}
                  </DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Source</DetailLabel>
                  <DetailValue>{selectedTicket.source === 'webapp' ? 'Webapp' : 'Mobile app'}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Submitted</DetailLabel>
                  <DetailValue>
                    {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : '—'}
                  </DetailValue>
                </DetailGroup>
              </DetailGrid>

              <DetailGroup>
                <DetailLabel>Description</DetailLabel>
                <DetailValue>{selectedTicket.description}</DetailValue>
              </DetailGroup>

              <AgentCard>
                <DetailGroup>
                  <DetailLabel>Agent analysis</DetailLabel>
                  <DetailValue>
                    {formatAppIssueAgentStatusLabel(selectedJob?.workflow_status)}
                    {selectedJob?.intent ? ` · ${formatIntentLabel(selectedJob.intent)}` : ''}
                    {selectedJob?.target_repo ? ` · ${selectedJob.target_repo}` : ''}
                    {selectedJob?.confidence ? ` · ${selectedJob.confidence} confidence` : ''}
                  </DetailValue>
                </DetailGroup>
                {selectedJob?.overview ? (
                  <DetailGroup>
                    <DetailLabel>Overview</DetailLabel>
                    <DetailValue>{selectedJob.overview}</DetailValue>
                  </DetailGroup>
                ) : (
                  <HelperText>
                    {selectedJob?.workflow_status === 'analyzing'
                      ? 'Cursor Cloud is analyzing this ticket…'
                      : 'No analysis yet. It starts automatically after submit.'}
                  </HelperText>
                )}
                {selectedJob?.suggested_fix ? (
                  <DetailGroup>
                    <DetailLabel>Suggested fix</DetailLabel>
                    <DetailValue>{selectedJob.suggested_fix}</DetailValue>
                  </DetailGroup>
                ) : null}
                {selectedJob?.suggested_reply ? (
                  <DetailGroup>
                    <DetailLabel>Suggested reply</DetailLabel>
                    <DetailValue>{selectedJob.suggested_reply}</DetailValue>
                  </DetailGroup>
                ) : null}
                {selectedJob?.feature_branch ? (
                  <DetailGroup>
                    <DetailLabel>Feature branch</DetailLabel>
                    <DetailValue>{selectedJob.feature_branch}</DetailValue>
                  </DetailGroup>
                ) : null}
                {selectedJob?.merge_sha ? (
                  <DetailGroup>
                    <DetailLabel>Merged SHA</DetailLabel>
                    <DetailValue>{selectedJob.merge_sha}</DetailValue>
                  </DetailGroup>
                ) : null}
                {selectedJob?.error ? (
                  <DetailGroup>
                    <DetailLabel>Agent error</DetailLabel>
                    <DetailValue>{selectedJob.error}</DetailValue>
                  </DetailGroup>
                ) : null}
                {selectedJob?.cursor_agent_id ? (
                  <AgentLink
                    href={`https://cursor.com/agents/${selectedJob.cursor_agent_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Cursor agent
                  </AgentLink>
                ) : null}
                <ButtonRow>
                  <Button
                    type="button"
                    onClick={() => void decideAgentJob('approve')}
                    disabled={!canApproveFix || decideAgentJobMutation.isPending}
                    title={
                      canApproveFix
                        ? 'Writes a feature branch. You still apply migrations and deploy edge functions.'
                        : 'Approve is available after analysis, when a target repo is assigned.'
                    }
                  >
                    {decideAgentJobMutation.isPending && decideAgentJobMutation.variables?.action === 'approve'
                      ? 'Starting fix...'
                      : 'Approve fix'}
                  </Button>
                  <DangerButton
                    type="button"
                    onClick={() => void decideAgentJob('reject')}
                    disabled={!canRejectJob || decideAgentJobMutation.isPending}
                  >
                    {decideAgentJobMutation.isPending && decideAgentJobMutation.variables?.action === 'reject'
                      ? 'Rejecting...'
                      : 'Reject'}
                  </DangerButton>
                  <SecondaryButton
                    type="button"
                    onClick={() => setReplyDraft(selectedJob?.suggested_reply?.trim() || '')}
                    disabled={!selectedJob?.suggested_reply?.trim()}
                  >
                    Use suggested reply
                  </SecondaryButton>
                </ButtonRow>
              </AgentCard>

              <Field>
                Status
                <Select
                  value={selectedStatus}
                  onChange={(event) => setSelectedStatus(event.target.value as AppIssueStatus)}
                >
                  {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <ButtonRow>
                <Button type="button" onClick={saveSelectedStatus} disabled={updateTicketMutation.isPending}>
                  {updateTicketMutation.isPending ? 'Saving...' : 'Save status'}
                </Button>
              </ButtonRow>

              <DetailGroup>
                <DetailLabel>Conversation</DetailLabel>
                {messagesLoading ? <HelperText>Loading replies…</HelperText> : null}
                <Thread>
                  {messages.length === 0 && !messagesLoading ? (
                    <HelperText>No replies yet.</HelperText>
                  ) : (
                    messages.map((message) => (
                      <ThreadItem key={message.id}>
                        <ThreadMeta>
                          {message.author_name || 'Support'} ·{' '}
                          {message.created_at ? new Date(message.created_at).toLocaleString() : '—'}
                        </ThreadMeta>
                        <DetailValue>{message.body}</DetailValue>
                      </ThreadItem>
                    ))
                  )}
                </Thread>
              </DetailGroup>

              <Field style={{ maxWidth: '100%' }}>
                Reply
                <Textarea
                  value={replyDraft}
                  onChange={(event) => setReplyDraft(event.target.value)}
                  placeholder="Ask a follow-up question or share a resolution"
                />
              </Field>

              <ButtonRow>
                <Button
                  type="button"
                  onClick={sendReply}
                  disabled={addMessageMutation.isPending || !replyDraft.trim()}
                >
                  {addMessageMutation.isPending ? 'Sending...' : 'Send reply'}
                </Button>
                <SecondaryButton type="button" onClick={closeTicket}>
                  Close
                </SecondaryButton>
              </ButtonRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default AppIssuesPage;
